import { observer } from "mobx-react-lite";
import React, { useMemo, useState } from "react";
import {
  Button,
  Container,
  Form,
  Grid,
  Message,
  Radio,
} from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { Formik } from "formik";
import { Link } from "react-router-dom";
import { useStore } from "../../../app/stores/store";
import LoadingComponent from "../../../app/layout/LoadingComponent";
import PageHeader from "../../../app/layout/PageHeader";
import SettingsNavBar from "../SettingsNavBar";
import SettingsDropdownMenuMobile from "../SettingsDropdownMenuMobile";
import { SettingsInventoryNotifyType } from "../../../app/models/enums";
import { SettingsInventoryDTO } from "../../../app/models/settings";
import {
  yesNoOptions,
} from "../../../app/common/options/option";
import MyTextInput from "../../../app/common/form/MyTextInput";
import MyHelpButton from "../../../app/common/form/MyHelpButton";

export default observer(function InventorySettings() {
  const { t } = useTranslation(["common", "ticketSettings"]);

  const { inventorySettingsStore } = useStore();

  const {
    inventorySettings,
    loadingInitial,
    loadInventorySettings,
    addInventorySettings,
    updateInventorySettings,
  } = inventorySettingsStore;

  const validationSchema = Yup.object({
    isActive: Yup.boolean().required(),
    enableNotifyLowInventory: Yup.boolean().required(),
    lowInventoryDefault: Yup.number().nullable(),
    enableCoreTracking: Yup.boolean().required(),
    notifyType: Yup.mixed().required(),
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [completed, setCompleted] = useState<boolean>(false);
  const [inactive, setInactive] = useState<boolean>(false);

  const inventorySettingsFormValues = useMemo(() => {
    setInactive(!(inventorySettings?.isActive ?? false));

    if (inventorySettings) return inventorySettings;

    return {
      id: undefined,
      isActive: undefined,
      enableNotifyLowInventory: undefined,
      lowInventoryDefault: 3,
      enableCoreTracking: false,
      notifyType: SettingsInventoryNotifyType.EveryWeekday,
    };
  }, [inventorySettings]);

  if (loadingInitial)
    <LoadingComponent
      content={t("formatting_loading", { ns: "ticketSettings" })}
    />;

  function handleFormSubmit(
    values: SettingsInventoryDTO,
    setSubmitting: Function
  ) {
    console.log(values);
    setSubmitting(true);
    setCompleted(true);
    if (!values.isActive) {
      values.enableNotifyLowInventory = false;
    }

    const mutation = values.id
      ? updateInventorySettings(values)
      : addInventorySettings(values);
    mutation
      .then(async () => await loadInventorySettings())
      .finally(() => setSubmitting(false));
  }

  return (
    <>
      <Grid stackable>
        <Grid.Column width={3} className="mobileHeight">
          <SettingsNavBar />
          <SettingsDropdownMenuMobile />
        </Grid.Column>
        <Grid.Column width={13}>
          <PageHeader
            header={`${t("inventory.inv_coreHeader", {
              ns: "settings",
            })}`}
            type={"h2"}
            divider={true}
            getAlerts={true}
            addTitle={true}
            showHelp
            helpName="settings.inventory.general"
          />
          <>
            <Container className="page">
              <Formik
                validationSchema={validationSchema}
                enableReinitialize
                initialValues={inventorySettingsFormValues}
                onSubmit={(values, { setSubmitting }) =>
                  handleFormSubmit(
                    {
                      id: inventorySettings?.id ?? values.id ?? undefined,
                      enableCoreTracking:
                        values.enableCoreTracking?.toString() === "true",
                      isActive: values.isActive?.toString() === "true",
                      lowInventoryDefault: values.lowInventoryDefault,
                      enableNotifyLowInventory:
                        values.enableNotifyLowInventory?.toString() === "true",
                      notifyType: values.notifyType,
                    },
                    setSubmitting
                  )
                }
              >
                {({
                  handleSubmit,
                  isValid,
                  isSubmitting,
                  dirty,
                  values,
                  handleChange,
                  handleBlur,
                  setFieldValue,
                }) => (
                  <Form
                    className="ui form"
                    autoComplete="off"
                    onSubmit={handleSubmit}
                  >
                    {completed && isValid && (
                      <Message positive>
                        {t("settingsupdated", {
                          ns: "common",
                        })}
                      </Message>
                    )}
                    {/* 
                        <ErrorMessage
                          name="error"
                          render={() => <ValidationErrors errors={errors. />}
                        /> 
                    */}
                    <Grid stackable>
                      <Grid.Row columns={2}>
                        <Grid.Column>
                          <label className="required">
                            {`${t("enableInventory", {
                              ns: "inventory",
                            })}:`}
                          </label>
                          <MyHelpButton
                            name="paymentMethodHelp"
                            help="settings.inventory.enableInventory"
                            position="bottom left"
                            color={"blue"}
                          />
                          <Form.Group inline>
                            {yesNoOptions.map((data, index) => (
                              <Form.Field key={`isActive[${data.value}]`}>
                                <Radio
                                  id={`isActive[${index}]`}
                                  key={`isActive[${data.value}]`}
                                  value={data.value}
                                  name="isActive"
                                  onChange={(e, d) => {
                                    const a = d.value === "false";
                                    setInactive(a);
                                    handleChange(e);
                                  }}
                                  onBlur={handleBlur}
                                  label={data.text}
                                  checked={
                                    values.isActive?.toString() === data.value
                                  }
                                />
                              </Form.Field>
                            ))}
                          </Form.Group>
                        </Grid.Column>
                        <Grid.Column>
                          <label className="required">
                            {`${t("enableCoreTracking", {
                              ns: "inventory",
                            })}:`}
                          </label>
                          <MyHelpButton
                            name="paymentMethodHelp"
                            help="settings.inventory.coreTracking"
                            position="bottom left"
                            color={"blue"}
                          />
                          <Form.Group inline>
                            {yesNoOptions.map((data, index) => (
                              <Form.Field
                                key={`enableCoreTracking[${data.value}]`}
                              >
                                <Radio
                                  id={`enableCoreTracking[${index}]`}
                                  key={`enableCoreTracking[${data.value}]`}
                                  value={data.value}
                                  name="enableCoreTracking"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  label={data.text}
                                  checked={
                                    values.enableCoreTracking?.toString() ===
                                    data.value
                                  }
                                />
                              </Form.Field>
                            ))}
                          </Form.Group>
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row columns={2}>
                        <Grid.Column>
                          <label className="required">
                            {`${t("inventory.enableNotifications", {
                              ns: "settings",
                            })}:`}
                          </label>
                          <MyHelpButton
                            name="paymentMethodHelp"
                            help="settings.inventory.lowInventoryReminders"
                            position="bottom left"
                            color={"blue"}
                          />
                          <Form.Group inline>
                            {yesNoOptions.map((data, index) => (
                              <Form.Field
                                key={`enableNotifyLowInventory[${data.value}]`}
                              >
                                <Radio
                                  id={`enableNotifyLowInventory[${index}]`}
                                  key={`enableNotifyLowInventory[${data.value}]`}
                                  value={data.value}
                                  name="enableNotifyLowInventory"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  label={data.text}
                                  checked={
                                    !inactive &&
                                    values.enableNotifyLowInventory?.toString() ===
                                      data.value
                                  }
                                  disabled={inactive}
                                />
                              </Form.Field>
                            ))}
                          </Form.Group>
                        </Grid.Column>
                        <Grid.Column>
                          <label>
                            {`${t("defaultQtyLowInventory", {
                              ns: "inventory",
                            })}:`}
                          </label>
                          <MyHelpButton
                            name="paymentMethodHelp"
                            help="settings.inventory.lowInventoryReminderQty"
                            position="bottom left"
                            color={"blue"}
                          />
                          <MyTextInput
                            placeholder="0"
                            name="lowInventoryDefault"
                            maxLength={500}
                          />
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column>
                          <Button
                            color="green"
                            className="save_button"
                            icon="check"
                            content={t("save", { ns: "common" })}
                            disabled={
                              isSubmitting ||
                              !dirty ||
                              !isValid ||
                              loadingInitial
                            }
                            loading={isSubmitting}
                            type="submit"
                            data-testid="save"
                          />
                          <Button
                            className="save_button"
                            as={Link}
                            to="/settings"
                            icon="cancel"
                            type="reset"
                            content={t("cancel", { ns: "common" })}
                          />
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  </Form>
                )}
              </Formik>
            </Container>
          </>
        </Grid.Column>
      </Grid>
    </>
  );
});
